import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';

import { QuickActivity } from '../interfaces/quick-activity.interfaces';
import { environment } from '../../environments/environment';

interface BackendQuickActivity {
  _id: string;
  title: string;
  description?: string;
  type: string;
  valueInHours: number;
  startTime?: string;
  endTime?: string;
  maxParticipants?: number | null;
  comments?: string;
  created?: string | Date;
  updated?: string | Date;
}

interface ApiListResponse {
  status: string;
  results: number;
  data: {
    documents: BackendQuickActivity[];
  };
}

interface ApiDocumentResponse {
  status: string;
  data: {
    document: BackendQuickActivity;
  };
}

function toDate(value: string | Date | undefined): Date {
  if (!value) return new Date();

  return value instanceof Date ? value : new Date(value);
}

function mapQuickActivity(
  doc: BackendQuickActivity,
): QuickActivity {
  return {
    id: doc._id,
    title: doc.title,
    description: doc.description ?? '',
    type: doc.type,
    valueInHours: doc.valueInHours,
    startTime: doc.startTime ?? '',
    endTime: doc.endTime ?? '',
    maxParticipants: doc.maxParticipants ?? null,
    comments: doc.comments ?? '',
    created: toDate(doc.created),
    updated: toDate(doc.updated),
  };
}

@Injectable({
  providedIn: 'root',
})
export class QuickActivityService {
  private readonly http = inject(HttpClient);

  private readonly baseUrl =
    `${environment.apiUrl}/quick-activities`;

  getAllQuickActivities(): Observable<QuickActivity[]> {
    return this.http
      .get<ApiListResponse>(`${this.baseUrl}/`)
      .pipe(
        map((res) =>
          res.data.documents.map(mapQuickActivity),
        ),
      );
  }

  createQuickActivity(
    quickActivity: Partial<QuickActivity>,
  ): Observable<QuickActivity> {
    return this.http
      .post<ApiDocumentResponse>(
        `${this.baseUrl}/`,
        quickActivity,
      )
      .pipe(
        map((res) =>
          mapQuickActivity(res.data.document),
        ),
      );
  }

  updateQuickActivity(
    id: string,
    quickActivity: Partial<QuickActivity>,
  ): Observable<QuickActivity> {
    return this.http
      .patch<ApiDocumentResponse>(
        `${this.baseUrl}/${id}`,
        quickActivity,
      )
      .pipe(
        map((res) =>
          mapQuickActivity(res.data.document),
        ),
      );
  }

  deleteQuickActivity(id: string): Observable<unknown> {
    return this.http.delete(
      `${this.baseUrl}/${id}`,
    );
  }

  publishQuickActivity(
    id: string,
    activity: {
      startDate: Date | string;
      dueDate: Date | string;
      startTime?: string;
      endTime?: string;
      maxParticipants?: number | null;
      comments?: string;
    },
  ): Observable<unknown> {
    return this.http.post(
      `${this.baseUrl}/${id}/publish`,
      activity,
    );
  }
}
